import { useEffect, useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { generatePath, useNavigate, useParams } from 'react-router-dom';
import copy from 'copy-to-clipboard';
import isEqual from 'lodash.isequal';
import {
  SidePanel,
  ButtonPrimary,
  ButtonPseudo,
  Popup,
  responseErrorsResolver,
  ResponseError,
  ValidatorsProps,
  ajvResolver,
  PermissionEnum,
  IconCopy,
  Tabs,
  ChangeLogs,
  PermissionDenied,
  SidePanelType,
  SelectItemProps,
  IconAvatar,
  SidePanelRow,
  ToggleInput,
  ToggleSize,
  IconLoader,
  SecurityLockForm,
  SecuritySidepanelLocksList,
  SecuritySidepanelLogsList,
  DeviceSessionSidepanel,
  IconSelected,
  IconClose,
} from '@ui/src';
import { useAppSelector, useAppDispatch } from '@store';
import ROUTES from '../../routes';
import {
  changePanelState,
  clearLockPanelState,
  clearSidepanelUserState,
  clearDeviceSession,
} from './store/slices/sidepanel';
import PanelUserContent from '../../components/user-list/panel/panel-user-content';
import {
  useCreateUserMutation,
  useEditUserMutation,
  useLazyGetUserQuery,
  useLazyGetUsersQuery,
  useEnableUserMutation,
  useDisabledUserMutation,
  useRecoveryPasswordMutation,
  useLazyGetUserChangeLogsQuery,
  useResetNotificationTemplateToRoleMutation,
  useResetNotificationTemplateToUserTemplateMutation,
  useLazyGetUserDevicesQuery,
  useEndUserSessionMutation,
  useLazyGetUserLockItemQuery,
  useLazyGetUserLocksQuery,
  useCreateUserLockMutation,
  useUpdateUserLockMutation,
  useLazyGetUserLockItemAuditLogsQuery,
  useLazyGetUserAuditLogsQuery,
  useLazyGetUserActiveLocksQuery,
  useLazyGetEmployeeDeviceSessionsQuery,
} from './store/services/users';
import {
  CreateUserRequest,
  EditUserRequest,
  FormValuesContent,
  UserStatuses,
  UserDetails as IUserDetails,
  SignatureType,
} from './types';
import {
  useLazyGetActiveCurrenciesQuery,
  useLazyGetAdministrationCountriesQuery,
  useLazyGetEmployeesBaseDictionariesQuery,
  useLazyGetEmployeesDictionariesQuery,
} from '../../store/dictionaries/service';
import { useLazyGetNotificationsTemplatesQuery } from '../notification-settings/store/services';
import UserDevice from '../../components/user-list/devices/device-item';
import {
  schema as logsSchema,
  columns,
  columnsLabels,
  filtersSchema,
} from './variables/lock-item-logs';
import {
  schema as locksSchema,
  columns as locksColumns,
  columnsLabels as locksColumnsLabels,
  filtersSchema as locksFiltersSchema,
} from './variables/locks';
import {
  schema as auditLogsSchema,
  columns as auditLogsColumns,
  columnsLabels as auditLogsColumnsLabels,
  filtersSchema as auditLogsFiltersSchema,
} from './variables/logs';
import { Device } from '../settings/profile/types';

import cn from './styles.module.css';

// const ipv4Regexp =
//   /^((25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.){3}(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;

const schema = {
  type: 'object',
  properties: {
    firstName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'First name is required',
    },
    lastName: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Last name is required',
    },
    employeeRoleTemplateId: {
      type: 'string',
      minLength: 1,
      errorMessage: 'Role is required',
    },
    email: {
      type: 'string',
      minLength: 1,
      format: 'email',
      errorMessage: 'Email is empty or incorrect format',
    },
  },
  required: [],
};

const defaultValues = {
  firstName: '',
  lastName: '',
  email: '',
  emailPrivate: '',
  phone: '',
  docType: '',
  docNumber: '',
  description: '',
  ipsWhitelist: [''],
  notificationTypeTemplateId: '',
  status: UserStatuses.ACTIVE,
  employeeRoleTemplateId: '',
  signatureType: SignatureType.NONE,
};

const UserDetails = (): React.JSX.Element => {
  const {
    userList: {
      sidepanel: {
        panelIsOpen,
        userInfo,
        validators,
        changeLogs,
        changeLogsPagination,
        devices,
        devicesPagination,
        lockItem,
        lockItemAuditLogs,
        lockItemAuditLogsPagination,
        locks,
        locksPagination,
        auditLogs,
        auditLogsPagination,
        activeLock,
        deviceSessionsPagination,
        deviceSessions,
      },
      users: { users: employees },
    },
    administrationDictionaries: {
      roles: roleOptions,
      activePermissions,
      roles,
      activeCurrencies,
      countries,
      users,
    },
    notificationSettings: {
      templates: { templates },
    },
  } = useAppSelector((state) => state);

  const { userId } = useParams();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [archivePopupVisible, setArchivePopupVisible] = useState(false);
  const [dirtyPopupVisible, setDirtyPopupVisible] = useState(false);
  const [copyIsSuccess, setCopyIsSuccess] = useState(false);
  const [addedUserTitle, setAddedUserTitle] = useState('');
  const [globalError, setGlobalError] = useState('');
  // const [invalidIpAddressesList, setInvaidIpAddressesList] = useState<string[]>(
  //   [],
  // );
  const [resetPasswordPopupVisible, setResetPasswordPopupVisible] =
    useState(false);
  const [recoveryToken, setRecoveryToken] = useState('');
  const [active, setActive] = useState(false);
  const [activeTab, setActiveTab] = useState<
    'details' | 'logs' | 'devices' | 'locks' | 'audit-logs'
  >('details');
  const [currentRole, setCurrentRole] = useState<SelectItemProps>();
  const [nextDevicesLoading, setNextDevicesLoading] = useState(false);
  const [endSessionId, setEndSessionId] = useState('');
  const [defaultFormValues, setDefaultFormValues] = useState<any>();
  const [lockFormVisible, setLockFormVisible] = useState(false);
  const [lockId, setLockId] = useState('');
  const [deviceSessionSidepanelVisible, setDeviceSessionSidepanelVisible] =
    useState(false);
  const [device, setDevice] = useState<Device | undefined>(undefined);

  const [getEmployeesDictionaries] = useLazyGetEmployeesDictionariesQuery();
  const [getEmployeesBaseDictionaries] =
    useLazyGetEmployeesBaseDictionariesQuery();
  const [getActiveCurrencies] = useLazyGetActiveCurrenciesQuery();
  const [getEmployeesCountries] = useLazyGetAdministrationCountriesQuery();
  const [getNotificationTemplates] = useLazyGetNotificationsTemplatesQuery();
  const [
    resetNotificationTemplateToRole,
    { isLoading: resetToRoleInProgress, isSuccess: resetToRoleIsSuccess },
  ] = useResetNotificationTemplateToRoleMutation();
  const [
    resetNotificationTemplateToUserTemplate,
    {
      isLoading: resetNotificationTemplateInProgress,
      isSuccess: resetNotificationTeplateIsSuccess,
    },
  ] = useResetNotificationTemplateToUserTemplateMutation();
  const [recoveryPassword, { isLoading: recoveryInProgress, isSuccess }] =
    useRecoveryPasswordMutation();
  const [
    getDevices,
    { isLoading: devicesIsLoading, isFetching: devicesIsFetching },
  ] = useLazyGetUserDevicesQuery();
  const [
    endSession,
    { isLoading: endSessionInProgress, isSuccess: endSessionIsSuccess },
  ] = useEndUserSessionMutation();

  const [create, { isLoading: createIsLoading }] = useCreateUserMutation();
  const [edit, { isLoading: editIsLoading, isSuccess: editIsSuccess }] =
    useEditUserMutation();
  const [getUser, { isLoading, isFetching }] = useLazyGetUserQuery();
  const [getUsers] = useLazyGetUsersQuery();
  const [
    getChangeLogs,
    { isLoading: changeLogsIsLoading, isFetching: changeLogsIsFetching },
  ] = useLazyGetUserChangeLogsQuery();
  const [
    enableUser,
    {
      isLoading: enableInProgress,
      isSuccess: enableIsSuccess,
      isError: enableIsError,
    },
  ] = useEnableUserMutation();
  const [
    disableUser,
    {
      isLoading: disableInProgress,
      isSuccess: disableIsSuccess,
      isError: disableIsError,
    },
  ] = useDisabledUserMutation();

  const [
    getLockItem,
    { isLoading: lockItemIsLoading, isFetching: lockItemIsFetching },
  ] = useLazyGetUserLockItemQuery();
  const [getLocks, { isLoading: locksIsLoading, isFetching: locksIsFetching }] =
    useLazyGetUserLocksQuery();
  const [
    createLock,
    { isLoading: crateLockRequestInProgress, isSuccess: createLockIsSuccess },
  ] = useCreateUserLockMutation();
  const [
    update,
    { isLoading: updateInProgress, isSuccess: updateLockIsSuccess },
  ] = useUpdateUserLockMutation();
  const [
    getLockItemLogsList,
    { isFetching: lockItemLogsIsFetching, isLoading: lockItemLogsIsLoading },
  ] = useLazyGetUserLockItemAuditLogsQuery();
  const [
    getListAuditLogs,
    { isFetching: auditLogsIsFetching, isLoading: auditLogsIsLoading },
  ] = useLazyGetUserAuditLogsQuery();
  const [getUserActiveLocks] = useLazyGetUserActiveLocksQuery();
  const [
    getDeviceSessions,
    { isLoading: deviceSessionsIsLoading, isFetching: deviceSessionIsFetching },
  ] = useLazyGetEmployeeDeviceSessionsQuery();

  const resetNotificationTemplateToRoleAction = async () => {
    await resetNotificationTemplateToRole(userInfo?.id as string);
  };

  const resetNotificationTemplateToUserTemplateAction = async () => {
    await resetNotificationTemplateToUserTemplate(userInfo?.id as string);
  };

  const onViewSessionClick = (deviceItem: Device) => {
    setDevice(deviceItem);
    setDeviceSessionSidepanelVisible(true);
    dispatch(changePanelState(false));
  };

  const onCloseDeviceSessionSidepanel = () => {
    setDevice(undefined);
    setDeviceSessionSidepanelVisible(false);
    dispatch(changePanelState(true));
    setActiveTab('devices');
    dispatch(clearDeviceSession());
  };

  const onCopy = () => {
    copy(`${window.location.host}/login/reset-password?token=${recoveryToken}`);

    setCopyIsSuccess(true);

    setTimeout(() => {
      setCopyIsSuccess(false);
    }, 2000);
  };

  const endSessionAction = (id: string) => {
    setEndSessionId(id);

    endSession({
      id: userId as string,
      deviceId: id,
    });
  };

  const getUsersAction = useCallback(() => {
    getUsers('');
  }, []);

  const getChangeLogsAction = useCallback(
    (cursor?: string) => {
      getChangeLogs(
        `?take=20&entityType=EMPLOYEE&entityId=${userId}${
          cursor ? `&cursor=${cursor}` : ''
        }`,
      );
    },
    [userId],
  );

  const getDevicesAction = useCallback(
    async (cursor?: string) => {
      if (cursor) {
        setNextDevicesLoading(true);
      }

      const response = await getDevices({
        id: userId as string,
        cursor: cursor || '',
      });

      if (response && response?.data) {
        setNextDevicesLoading(false);
      }
    },
    [userId],
  );

  const getUserAction = useCallback(() => {
    getUser(userId as string);
  }, [userId]);

  const {
    control,
    reset,
    formState: { errors },
    watch,
    setError,
    // setValue,
    getValues,
  } = useForm<FormValuesContent>({
    resolver: ajvResolver(
      userId
        ? (validators?.updateEmployeeSchema as ValidatorsProps)
        : (schema),
    ),
    defaultValues,
    mode: 'onChange',
  });

  const fields = watch();

  // const ipFields = watch('ipsWhitelist');

  const isDirty = !isEqual(fields, defaultFormValues);

  const handleClose = useCallback(() => {
    dispatch(clearSidepanelUserState());

    setTimeout(() => {
      navigate(ROUTES.USER_LIST);
    }, 100);
  }, [dispatch]);

  const onRequest = async (
    body: CreateUserRequest | EditUserRequest,
    id?: string,
  ) => {
    let response;

    setGlobalError('');

    const values = getValues();

    if (id) {
      response = (await edit({ body, id })) as {
        data?: { data: IUserDetails };
        error: ResponseError;
      };
    } else {
      response = (await create(body)) as {
        data?: { data: IUserDetails };
        error: ResponseError;
      };
    }

    if (response.error) {
      responseErrorsResolver({
        errors: response.error,
        setError,
        setGlobalError,
      });
    } else if (!response.error) {
      if (id) {
        setDefaultFormValues(fields);
      }

      if (!id) {
        let passwordResetAvailable = true;
        let notificationTemplateResponse;

        if (values?.notificationTypeTemplateId) {
          passwordResetAvailable = false;

          notificationTemplateResponse = (await edit({
            body: {
              ...body,
              notificationTypeTemplateId: values.notificationTypeTemplateId,
            },
            id: response?.data?.data.id as string,
          })) as {
            data?: { data: IUserDetails };
            error: ResponseError;
          };

          if (notificationTemplateResponse) {
            passwordResetAvailable = true;

            if (notificationTemplateResponse?.error) {
              responseErrorsResolver({
                errors: notificationTemplateResponse.error,
                setError,
                setGlobalError,
              });
            }
          }
        }

        if (passwordResetAvailable) {
          const passwordResponse = (await recoveryPassword({
            userId: response?.data?.data.id as string,
          })) as {
            error: any;
            data: { data: { token: string } };
          };

          if (
            !passwordResponse.error &&
            passwordResponse.data &&
            passwordResponse.data.data
          ) {
            setResetPasswordPopupVisible(true);
            setAddedUserTitle(`User ${body.firstName} ${body.lastName} added`);
            setRecoveryToken(passwordResponse.data.data.token);

            if (
              notificationTemplateResponse &&
              !notificationTemplateResponse.error
            ) {
              dispatch(changePanelState(false));
            }
          }
        }
      }
    }
  };

  const onSubmit = () => {
    setGlobalError('');

    const values = getValues();
    // let invalidIpAddresses: string[] = [];
    // if (values?.ipsWhitelist?.length) {
    //   values?.ipsWhitelist.forEach((item) => {
    //     if (!item.match(ipv4Regexp)) {
    //       invalidIpAddresses.push(item);
    //     } else if (
    //       invalidIpAddresses.find((ip) => ip === item) &&
    //       item.match(ipv4Regexp)
    //     ) {
    //       invalidIpAddresses = invalidIpAddresses.filter((ip) => ip !== item);
    //     }
    //   });

    //   setInvaidIpAddressesList(invalidIpAddresses);
    // }

    // if (!invalidIpAddresses.filter((item) => item).length) {
    //   setInvaidIpAddressesList([]);

    //   const body = {
    //     id: userId || undefined,
    //     employeeRoleTemplateId: values.employeeRoleTemplateId || undefined,
    //     ipsWhitelist: values?.ipsWhitelist?.filter((item) => item) || [],
    //     firstName: values.firstName || undefined,
    //     lastName: values.lastName || undefined,
    //     email: values.email || undefined,
    //     emailPrivate: values.emailPrivate || undefined,
    //   };

    //   onRequest(body, userId);
    // }

    const body: any = {
      id: userId || undefined,
      employeeRoleTemplateId: values.employeeRoleTemplateId || undefined,
      // ipsWhitelist: values?.ipsWhitelist?.filter((item) => item) || [],
      firstName: values.firstName || undefined,
      lastName: values.lastName || undefined,
      email: values.email || undefined,
      emailPrivate: values.emailPrivate || undefined,
      canSignTransactions: Boolean(
        values.signatureType === SignatureType.SINGLE,
      ),
    };

    if (userId && values.notificationTypeTemplateId) {
      body.notificationTypeTemplateId = values.notificationTypeTemplateId;
    }

    onRequest(body, userId);
  };

  const onFormStatusSubmit = async () => {
    await disableUser(userId as string);
  };

  const onCheckStatus = () => {
    if (!active && userId && userInfo?.status === UserStatuses.ACTIVE) {
      setArchivePopupVisible(true);
    } else {
      onSubmit();
    }
  };

  const onResetPassword = async () => {
    const response = (await recoveryPassword({
      userId: userId as string,
    })) as {
      data: { data: { token: string } };
    };

    if (response && response.data && response.data.data) {
      setRecoveryToken(response.data.data.token);
    }
  };

  const onChangeStatus = () => {
    if (active) {
      setActive(false);
      setArchivePopupVisible(true);
    } else {
      enableUser(userId as string);
    }
  };

  useEffect(() => {
    if (endSessionIsSuccess) {
      setEndSessionId('');
    }
  }, [endSessionIsSuccess]);

  useEffect(() => {
    if (
      !templates &&
      activePermissions &&
      activePermissions?.includes(
        PermissionEnum.NOTIFICATIONS_TYPES_TEMPLATE_LIST,
      )
    ) {
      getNotificationTemplates();
    }
  }, [templates, activePermissions]);

  useEffect(() => {
    if (
      activeTab === 'logs' &&
      !changeLogs &&
      activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST)
    ) {
      getChangeLogsAction();
    }
  }, [activeTab]);

  useEffect(() => {
    if (
      activeTab === 'devices' &&
      !devices &&
      activePermissions?.includes(PermissionEnum.EMPLOYEES_DEVICES_LIST)
    ) {
      getDevicesAction();
    }
  }, [activeTab]);

  useEffect(() => {
    if (!roles) {
      getEmployeesBaseDictionaries();
    }

    if (!activeCurrencies) {
      getActiveCurrencies();
    }

    if (!countries) {
      getEmployeesCountries();
    }
  }, [roles, activeCurrencies, countries]);

  useEffect(() => {
    if (userInfo) {
      reset(userInfo);

      setDefaultFormValues(userInfo);
      setActive(Boolean(userInfo.status === UserStatuses.ACTIVE));
    } else {
      reset(defaultValues);
      setDefaultFormValues(defaultValues);
    }
  }, [reset, userInfo]);

  useEffect(() => {
    dispatch(changePanelState(true));

    return () => {
      dispatch(clearSidepanelUserState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (userId && panelIsOpen) {
      reset();
      getUserAction();
    }
  }, [panelIsOpen, userId, getUserAction]);

  useEffect(() => {
    if (editIsSuccess || disableIsSuccess) {
      if (disableIsSuccess) {
        handleClose();
      }

      setTimeout(() => {
        getUsersAction();
      }, 100);
    }
  }, [editIsSuccess, handleClose, disableIsSuccess]);

  useEffect(() => {
    if (enableIsSuccess) {
      setActive(true);

      setTimeout(() => {
        getUsersAction();
      }, 100);
    }
  }, [enableIsSuccess]);

  useEffect(() => {
    if (disableIsError) {
      setArchivePopupVisible(false);

      setActive(true);
    }
  }, [disableIsError]);

  useEffect(() => {
    if (enableIsError) {
      setActive(false);
    }
  }, [disableIsError]);

  const isEditable = activePermissions?.includes(
    PermissionEnum.USERS_EDIT_ACTION,
  );

  let buttonText: string;

  if (createIsLoading || editIsLoading || (!userId && recoveryInProgress)) {
    buttonText = 'Loading...';
  } else {
    buttonText = userId ? 'Save' : 'Add';
  }

  useEffect(() => {
    if (userInfo && roleOptions) {
      const currentRoleTemplate = roleOptions?.find(
        (item) => item.value === userInfo?.employeeRoleTemplateId,
      );

      setCurrentRole(currentRoleTemplate);
    }
  }, [userInfo, roleOptions]);

  useEffect(() => {
    if (activePermissions?.includes(PermissionEnum.USERS_LIST) && !users) {
      getEmployeesDictionaries();
    }
  }, [activePermissions, users]);

  useEffect(() => {
    if (isSuccess && userId) {
      setResetPasswordPopupVisible(true);
    }
  }, [isSuccess, userId]);

  useEffect(() => {
    if (userId || createLockIsSuccess || updateLockIsSuccess) {
      getUserActiveLocks(userId as string);
    }
  }, [userId, createLockIsSuccess, updateLockIsSuccess]);

  return (
    <>
      <Popup
        title={`Move ${userInfo?.firstName} ${userInfo?.lastName} to Archive?`}
        description="All accesses of this user will be reset"
        onCancelClick={() => {
          setArchivePopupVisible(false);
          setActive(true);
        }}
        isVisible={archivePopupVisible}
        onOkClick={onFormStatusSubmit}
        cancelButtonText="Cancel"
        okButtonText={disableInProgress ? 'Loading...' : 'Yes'}
        parent={document.getElementById('archive-popup') as HTMLElement}
      />
      <SecurityLockForm
        getLockItem={getLockItem}
        getLocks={getLocks}
        create={createLock}
        update={update}
        lockItem={lockItem}
        takeCount={20}
        activePermissions={activePermissions}
        employees={employees}
        clearLockPanelState={() => {
          dispatch(clearLockPanelState());
        }}
        onClose={() => {
          setLockFormVisible(false);

          dispatch(changePanelState(true));
          setLockId('');
        }}
        onBlockUserClick={() => {
          setLockFormVisible(true);

          dispatch(changePanelState(false));
        }}
        isSuccess={createLockIsSuccess}
        isLoading={lockItemIsLoading}
        isFetching={lockItemIsFetching}
        userId={userId}
        requestInProgress={crateLockRequestInProgress}
        updateInProgress={updateInProgress}
        lockIdValue={lockId}
        formPanelVisible={lockFormVisible}
        lockItemAuditLogsPagination={lockItemAuditLogsPagination}
        lockItemAuditLogs={lockItemAuditLogs}
        getLockItemLogsList={getLockItemLogsList}
        lockItemLogsSchema={logsSchema}
        lockItemLogsFiltersSchema={filtersSchema}
        lockItemLogsColumns={columns}
        lockItemLogsColumnsLabels={columnsLabels}
        lockItemLogsIsLoading={lockItemLogsIsLoading}
        lockItemLogsIsFetching={lockItemLogsIsFetching}
        withoutUserSelector
      />
      <DeviceSessionSidepanel
        panelIsOpen={deviceSessionSidepanelVisible}
        handleClose={onCloseDeviceSessionSidepanel}
        deleteSessionIsAvailable={activePermissions?.includes(
          PermissionEnum.EMPLOYEES_SESSIONS_DELETE_ACTION,
        )}
        isLoading={endSessionInProgress}
        onEndSession={() => endSessionAction(device?.id as string)}
        device={device}
        itemId={userId}
        deviceId={device?.id}
        deviceSessions={deviceSessions}
        activePermissions={activePermissions}
        isFetching={deviceSessionIsFetching}
        deviceSessionsIsLoading={deviceSessionsIsLoading}
        deviceSessionsPagination={deviceSessionsPagination}
        auditLink={ROUTES.SECURITY_LOGS_ITEM}
        getDeviceSessions={getDeviceSessions}
        sessionIsAvailable={activePermissions?.includes(
          PermissionEnum.EMPLOYEES_DEVICE_SESSIONS_LIST,
        )}
      />
      <SidePanel
        closeOnClickOutside
        isOpen={panelIsOpen}
        onClose={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
        title={userId ? 'User details' : 'Add user'}
        errorSidepanel={globalError}
        type={SidePanelType.Accounts}
        sidepanelExpandedKey="user-details"
        footer={
          <>
            {(isEditable || !userId) && (
              <ButtonPrimary
                text={buttonText}
                onClick={onCheckStatus}
                disabled={
                  createIsLoading ||
                  editIsLoading ||
                  !isDirty ||
                  Object.keys(errors).length > 0 ||
                  (!userId && recoveryInProgress)
                }
                bold
              />
            )}

            <ButtonPseudo
              text="Cancel"
              onClick={isDirty ? () => setDirtyPopupVisible(true) : handleClose}
              bold
            />
          </>
        }
      >
        <>
          {userId && (
            <>
              <div className={cn.userDetailsTop}>
                {userInfo?.avatarImg ? (
                  <img
                    alt={`${userInfo?.firstName} ${userInfo?.lastName} avatar`}
                    src={userInfo?.avatarImg}
                    className={cn.avatar}
                  />
                ) : (
                  <IconAvatar className={cn.avatar} />
                )}
              </div>
              <SidePanelRow>
                <>
                  <ToggleInput
                    className={cn.toggle}
                    title="Active user"
                    size={ToggleSize.MEDIUM}
                    name="active"
                    checked={active}
                    value={active ? UserStatuses.ACTIVE : UserStatuses.DISABLED}
                    onChange={onChangeStatus}
                    disabled={
                      Boolean(userId && !isEditable) ||
                      enableInProgress ||
                      !activePermissions?.includes(
                        PermissionEnum.USERS_EDIT_STATUS_ACTION,
                      )
                    }
                    tabIndex={userId && !isEditable ? -1 : 0}
                  />
                  {userInfo && (
                    <div className={cn.totpInfo}>
                      {userInfo?.totpEnabled ? (
                        <IconSelected />
                      ) : (
                        <IconClose
                          width={15}
                          height={15}
                          className={cn.disabled}
                        />
                      )}
                      TOTP-confirmation{' '}
                      {userInfo?.totpEnabled ? 'enabled' : 'not enabled'}
                    </div>
                  )}
                </>
              </SidePanelRow>
            </>
          )}
          {userId && (
            <Tabs
              tabs={[
                {
                  name: 'Details',
                  value: 'details',
                },
                {
                  name: 'Locks',
                  value: 'locks',
                },
                {
                  name: 'Audit Logs',
                  value: 'audit-logs',
                },
                {
                  name: 'Devices',
                  value: 'devices',
                },
                {
                  name: 'Change log',
                  value: 'logs',
                },
              ]}
              activeTab={activeTab}
              itemClassName={cn.userTab}
              onClick={(tab) =>
                setActiveTab(
                  tab as
                    | 'details'
                    | 'logs'
                    | 'devices'
                    | 'locks'
                    | 'audit-logs',
                )
              }
            />
          )}
          {activeTab === 'details' && (
            <PanelUserContent
              control={control}
              errors={errors}
              roleOptions={roleOptions}
              userInfo={userInfo}
              isLoading={isLoading || isFetching}
              // ipFields={ipFields || ['']}
              isEditable={isEditable}
              // setValue={setValue}
              userId={userId}
              onResetPassword={isEditable ? onResetPassword : undefined}
              isCreatePanel={Boolean(!userId)}
              recoveryInProgress={recoveryInProgress}
              // invalidIpAddressesList={invalidIpAddressesList}
              auditLogAvailable={activePermissions?.includes(
                PermissionEnum.AUDIT_LOG_LIST,
              )}
              templates={templates
                ?.filter((item) => item.active)
                ?.map((item) => ({
                  name: item.name,
                  value: item.id,
                }))}
              notificationTemplatesAvailable={activePermissions?.includes(
                PermissionEnum.USERS_EDIT_NOTIFICATIONS_TYPES_TEMPLATE_ACTION,
              )}
              differentNotificationTemplate={Boolean(
                currentRole &&
                currentRole.suffix !== userInfo?.notificationTypeTemplateId,
              )}
              resetToRoleInProgress={resetToRoleInProgress}
              resetToRoleIsSuccess={resetToRoleIsSuccess}
              resetNotificationTemplateInProgress={
                resetNotificationTemplateInProgress
              }
              resetNotificationTeplateIsSuccess={
                resetNotificationTeplateIsSuccess
              }
              resetNotificationTemplateToRole={
                resetNotificationTemplateToRoleAction
              }
              resetNotificationTemplateToUserTemplate={
                resetNotificationTemplateToUserTemplateAction
              }
              activeLock={activeLock}
              onViewLocksClick={() => {
                setActiveTab('locks');

                navigate(
                  `${generatePath(ROUTES.USER_EDIT, {
                    userId: (userId as string) || '',
                  })}?locked=true`,
                );

                if (locks) {
                  getLocks(`?take=20&userId=${userId}&locked=true`);
                }
              }}
            />
          )}
          {activeTab === 'audit-logs' && (
            <>
              {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <SecuritySidepanelLogsList
                  auditLogs={auditLogs}
                  auditLogsPagination={auditLogsPagination}
                  panelIsOpen={panelIsOpen}
                  activePermissions={activePermissions}
                  userId={userId}
                  getList={getListAuditLogs}
                  schema={auditLogsSchema}
                  filtersSchema={auditLogsFiltersSchema}
                  isFetching={auditLogsIsFetching}
                  isLoading={auditLogsIsLoading}
                  columns={auditLogsColumns}
                  columnsLabels={auditLogsColumnsLabels}
                />
              )}
              {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <PermissionDenied />
              )}
            </>
          )}
          {activeTab === 'logs' && (
            <>
              {activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <ChangeLogs
                  entityType="EMPLOYEE"
                  entityId={userId as string}
                  changeLogsIsLoading={changeLogsIsLoading}
                  changeLogs={changeLogs}
                  activePermission={activePermissions}
                  users={users}
                  pagination={changeLogsPagination}
                  changeLogsIsFetching={changeLogsIsFetching}
                  getChangeLogs={getChangeLogsAction}
                />
              )}
              {!activePermissions?.includes(PermissionEnum.AUDIT_LOG_LIST) && (
                <PermissionDenied />
              )}
            </>
          )}
          {activeTab === 'locks' && (
            <>
              {activePermissions?.includes(PermissionEnum.LOCKS_LIST) && (
                <SecuritySidepanelLocksList
                  users={employees}
                  locks={locks}
                  locksPagination={locksPagination}
                  userId={userId}
                  getList={getLocks}
                  schema={locksSchema}
                  filtersSchema={locksFiltersSchema}
                  setLockId={setLockId}
                  onBlockUserClick={() => {
                    setLockFormVisible(true);

                    dispatch(changePanelState(false));
                  }}
                  isFetching={locksIsFetching}
                  activePermissions={activePermissions}
                  panelIsOpen={panelIsOpen}
                  isLoading={locksIsLoading}
                  columns={locksColumns}
                  columnsLabels={locksColumnsLabels}
                />
              )}
              {!activePermissions?.includes(PermissionEnum.LOCKS_LIST) && (
                <PermissionDenied />
              )}
            </>
          )}
          {activeTab === 'devices' && (
            <>
              {!nextDevicesLoading &&
                (devicesIsLoading || devicesIsFetching) && (
                  <div className={cn.devicesLoader}>
                    <IconLoader />
                  </div>
                )}
              {activePermissions?.includes(
                PermissionEnum.EMPLOYEES_DEVICES_LIST,
              ) &&
                !devicesIsLoading &&
                (!devicesIsFetching ||
                  (nextDevicesLoading && devicesIsFetching)) && (
                  <div className={cn.devicesWrapper}>
                    {devices?.map((item) => (
                      <UserDevice
                        key={item.id}
                        device={item}
                        endSessionsIsAvailable={activePermissions?.includes(
                          PermissionEnum.EMPLOYEES_SESSIONS_DELETE_ACTION,
                        )}
                        endSession={endSessionAction}
                        endSessionId={endSessionId}
                        endSessionInProgress={endSessionInProgress}
                        sessionsIsAvailable={activePermissions?.includes(
                          PermissionEnum.EMPLOYEES_DEVICE_SESSIONS_LIST,
                        )}
                        onViewSessionClick={onViewSessionClick}
                      />
                    ))}
                    {devicesPagination?.hasNextPage && (
                      <button
                        type="button"
                        className={cn.showMore}
                        onClick={() =>
                          getDevicesAction(devicesPagination.endCursor)
                        }
                      >
                        {nextDevicesLoading ? 'Loading...' : 'Show More'}
                      </button>
                    )}
                  </div>
                )}
              {!activePermissions?.includes(
                PermissionEnum.EMPLOYEES_DEVICES_LIST,
              ) && <PermissionDenied />}
            </>
          )}
        </>
      </SidePanel>
      <Popup
        title="You have unsaved changes that will be lost"
        description="Are you sure you want to close this form?"
        onCancelClick={() => setDirtyPopupVisible(false)}
        onOkClick={handleClose}
        cancelButtonText="Cancel"
        okButtonText="Close"
        isVisible={dirtyPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
      />
      <Popup
        title={userId ? 'Reset user password' : addedUserTitle}
        description="Send this link to the user for password recovery"
        onCancelClick={() => {
          setResetPasswordPopupVisible(false);

          if (!userId) {
            handleClose();
          }
        }}
        isVisible={resetPasswordPopupVisible}
        parent={document.getElementById('dirty-popup') as HTMLElement}
        cancelButtonText="Close"
      >
        <div className={cn.resetPasswordLink}>
          <div
            className={`${cn.copySuccess} ${copyIsSuccess ? cn.visible : ''}`}
          >
            Copied!
          </div>
          <span>{`${window.location.host}/login/reset-password?token=${recoveryToken}`}</span>
          <button
            type="button"
            className={cn.copyButton}
            onClick={onCopy}
            aria-label="Copy"
          >
            <IconCopy />
          </button>
        </div>
      </Popup>
      <div id="dirty-popup" />
    </>
  );
};

export default UserDetails;
